import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import {
  api,
  ApiError,
  ATTENDANCE_DAY_QUERY_KEY,
  ATTENDANCE_ME_QUERY_KEY,
  ATTENDANCE_TEAM_QUERY_KEY,
  type MyAttendance,
  type PunchType,
} from '../lib/api';
import { ACTION_LABEL, liveTotals, newPunchRef } from '../lib/attendance';

/**
 * Eigener Anwesenheitsstatus (Kopfzeile, Startseite, Hinweise) und Stempeln (v1.405.0).
 *
 * Eine Referenz je gemeinter Aktion: Bricht die Verbindung ab, nimmt der nächste Tipp auf denselben
 * Knopf dieselbe Referenz — kam der erste doch an, entsteht kein zweiter Stempel. Nach einer Antwort
 * (angenommen oder abgelehnt) gibt es für den nächsten Tipp eine neue.
 */
export function useAttendance() {
  const queryClient = useQueryClient();
  const refs = useRef<Partial<Record<PunchType, string>>>({});
  const query = useQuery({
    queryKey: ATTENDANCE_ME_QUERY_KEY,
    queryFn: () => api.getMyAttendance(),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const punch = useMutation({
    mutationFn: (type: PunchType) => {
      const ref = refs.current[type] ?? newPunchRef();
      refs.current[type] = ref;
      return api.punchAttendance(type, ref);
    },
    onSuccess: (res, type) => {
      delete refs.current[type];
      queryClient.setQueryData<MyAttendance>(ATTENDANCE_ME_QUERY_KEY, res.status);
      void queryClient.invalidateQueries({ queryKey: ATTENDANCE_TEAM_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ATTENDANCE_DAY_QUERY_KEY });
      notifications.show({ color: 'green', message: `${ACTION_LABEL[type]}: erledigt` });
      // Rückmeldung, wenn Jibble vor dem Stempel etwas anderes sagte (v1.416.1).
      if (res.jibbleNotice) {
        notifications.show({ color: 'orange', title: 'Hinweis aus Jibble', message: res.jibbleNotice, autoClose: 12_000 });
      }
    },
    onError: (err: Error, type) => {
      const answered = err instanceof ApiError && typeof err.status === 'number';
      if (answered) delete refs.current[type];
      void queryClient.invalidateQueries({ queryKey: ATTENDANCE_ME_QUERY_KEY });
      notifications.show({
        color: 'red',
        title: `${ACTION_LABEL[type]} nicht möglich`,
        message: err instanceof ApiError ? err.summary || err.message : `${err.message} — bitte noch einmal tippen.`,
      });
    },
  });

  return {
    status: query.data ?? null,
    isLoading: query.isLoading,
    fetchedAt: query.dataUpdatedAt,
    punch: punch.mutate,
    punching: punch.isPending ? punch.variables ?? null : null,
  };
}

/** Laufende Tageszeiten, minütlich neu gerechnet. */
export function useLiveTotals(status: MyAttendance | null, fetchedAt: number) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(t);
  }, []);
  const compute = useCallback(
    () => (status ? liveTotals(status, fetchedAt || now, now) : { workMinutes: 0, breakMinutes: 0 }),
    [status, fetchedAt, now],
  );
  return compute();
}
